import { inject, Injectable } from '@angular/core';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { NexusApiService } from '@df/api/nexus/nexus-api.service';
import { App } from '@df/core/app';
import { Acl } from '@df/core/service/acl';
import { TimeoutService } from '@df/ng/timeout.service';
import { MultisiteTranslation } from '@df/ui/translation/translation.interfaces';
import keyBy from 'lodash-es/keyBy';
import mapKeys from 'lodash-es/mapKeys';
import mapValues from 'lodash-es/mapValues';

export const HTML_CODE_REGEX = /&#(\d+);/g;

@Injectable({
    providedIn: 'root'
})
export class TranslationService {
    /**
     * List of missing translations
     */
    private missing: MultisiteTranslation.IMissingText[] = [];

    /**
     * Sends request to BE to save missing texts in translation file
     */
    private submitMissing: () => void;

    private translationsNormalized?: { [key: string]: string };

    /**
     * If debug mode is active we see all translted copies as *****
     */
    private isDebugMode = typeof document !== 'undefined' && document.cookie.match(/translationDebugOn/);

    /**
     * Switched off trasnlations
     */
    private isOff = typeof document !== 'undefined' && document.cookie.match(/translationOff/);

    private nexusApiService = inject(NexusApiService);
    private app = inject(App);
    private acl = inject(Acl);
    private timeoutService = inject(TimeoutService);
    private dfApiConfigService = inject(DfApiConfigService);

    /**
     * Never submit on development
     * do submit for developer and every 100th visitor
     */
    private canSubmit = this.app.dfConfig.environment !== 'development' && this.app.isPlatformBrowser && this.acl.check('cms');

    /**
     * Class constructor
     */
    constructor() {
        this.submitMissing = this.timeoutService.debounce(() => {
            if (!this.canSubmit || !this.missing.length) {
                return;
            }

            // reset array
            const terms = this.missing;
            this.missing = [];

            // send post with missing strings
            this.nexusApiService.translate.missing({ terms });
        }, 100);
    }

    /**
     * Translations getter
     */
    get translations() {
        if (!this.translationsNormalized) {
            const translations = this.dfApiConfigService.apiConfig!.translations;
            const translationsNormalized = Array.isArray(translations) ? mapValues(keyBy(translations, 'from'), 'to') : translations || {};
            this.translationsNormalized = mapValues(
                mapKeys(translationsNormalized, (value: string, key: string) => (typeof key === 'string' ? key.trim() : key)),
                (value: string) => (typeof value === 'string' ? value.trim() : value)
            );
        }
        return this.translationsNormalized;
    }

    /**
     * Translates a string copy
     */
    translate(text: string): string {
        if (typeof text !== 'string' || this.isOff) {
            return text;
        }

        if (this.isDebugMode) {
            return '*****';
        }

        // trim it to avoid problems with white spaces at the end or beggining
        text = text.trim();

        const textlc = text.toLowerCase();

        const getTranslation = t => {
            if (this.translations.hasOwnProperty(t)) {
                return this.translations[t];
            }
        };

        const translation = getTranslation(text) || (getTranslation(textlc) === textlc ? text : getTranslation(textlc));
        // if we don't have translation then pass it to buffer and return original copy
        if (!translation) {
            this.recordMissing(text);
        }

        return translation || text;
    }

    transform(text: string, ...params: any[]): string {
        const translatedCopy = this.translate(text);

        if (params?.length) {
            let index = 0;
            return translatedCopy.replace(/\%(\d)*/g, (fullString, requestedIndex) => {
                let readIndex: number;
                if (requestedIndex) {
                    readIndex = parseInt(requestedIndex, 10) - 1;
                } else {
                    readIndex = index;
                    index++;
                }
                const param = params[readIndex];
                if (typeof param === 'number' && param === 0) {
                    return param;
                }
                return param || '';
            });
        }

        return (
            (typeof translatedCopy === 'string'
                ? translatedCopy?.replace(HTML_CODE_REGEX, (fullString, code) => {
                      return String.fromCharCode(parseInt(code, 10));
                  })
                : translatedCopy) || ''
        );
    }

    private recordMissing(text: string) {
        if (this.app.isPlatformBrowser) {
            // next time just return source copy
            this.translations[text] = text;

            // buffer that missing text
            this.missing.push({
                copy: text,
                path: location.pathname
            });

            // call save
            this.submitMissing();
        }
    }
}
